import React, { useState, useEffect, useRef } from "react";
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from "react-redux";
import { setFile, setError, clearFile } from "../../../Redux/Slice/FileUpload";
import { setField } from "../../../Redux/Slice/SignUp";
import ImageCropper from "../ImageCropper/ImageCropper";
import { useGraphQL } from '../../../context/GraphQLProvider';
import { FileUploadQuery } from "../../../services/FileUploadService";
import Alerts from "../Alerts/Alerts";
import CircularProgress from "../CircularProgress/CircularProgress";
import { gql } from '@apollo/client';
import AuxiliaryText from "../Typography/AuxiliaryText";
import imageCompression from 'browser-image-compression';
import { validateFile, getDropzoneAcceptedTypes, getMaxSizeBytes } from "../../../utils/contentSafety";
import { FIELD_UPLOAD_FULL } from "../InputField/fieldSystem";
import { adminWizardUploadClasses } from "../../../sections/AdminDashboard/shared/adminUx";

const DropzoneUploader = ({ 
    acceptedFileTypes,
    maxFileSize,
    isImage = true,
    enableCropper = false,
    maxFiles = 1,
    loadingType = "circular", // "circular" or "progress"
    onUploadComplete,
    onUploadProgress,
    currentFileCount = 0,
    applyFieldStyles = true, // Control whether to apply tp-fields class
    fullWidth = false,
    dashboardUpload = false,
}) => {
    const dispatch = useDispatch();
    const fileContext = isImage ? 'image' : 'document';
    const defaultAccepted = getDropzoneAcceptedTypes(fileContext);
    const defaultMaxSize = getMaxSizeBytes(fileContext);
    const effectiveAcceptedTypes = acceptedFileTypes ?? defaultAccepted;
    const effectiveMaxFileSize = maxFileSize ?? defaultMaxSize;
    const graphqlService = useGraphQL();
    const { originalFile, croppedFile, error } = useSelector((state) => state.files);
    const [showCropper, setShowCropper] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const abortControllersRef = useRef({});
    const progressIntervalsRef = useRef({});

    // Cleanup intervals when component unmounts
    useEffect(() => {
        return () => {
            Object.values(progressIntervalsRef.current).forEach(interval => clearInterval(interval));
            Object.values(abortControllersRef.current).forEach(controller => controller.abort());
        };
    }, []);

    const simulateProgress = (fileId) => {
        let progress = 0;
        const interval = setInterval(() => {
            progress += Math.floor(Math.random() * 0.0000001); // Random increment between 0-9
            if (progress >= 90) {
                clearInterval(interval);
                return;
            }
            setUploadingFiles(prev => {
                const newFiles = [...prev];
                const fileIndex = newFiles.findIndex(f => f.id === fileId);
                if (fileIndex !== -1) {
                    newFiles[fileIndex] = {
                        ...newFiles[fileIndex],
                        progress: progress
                    };
                }
                return newFiles;
            });
        }, 300); // Update every 300ms

        progressIntervalsRef.current[fileId] = interval;
    };

    const handleFileProcessed = async (fileOrBlobUrl, fileId) => {
        if (!fileOrBlobUrl) {
            dispatch(setError("No file selected"));
            return;
        }

        try {
            let file;
            if (isImage && enableCropper) {
                const response = await fetch(fileOrBlobUrl);
                const blob = await response.blob();
                file = new File([blob], 'Profile-image.jpg', { type: blob.type });
            } else {
                // Replace spaces and special characters with hyphens in the file name
                const fileName = fileOrBlobUrl.name.replace(/[^a-zA-Z0-9.-]/g, '-');
                file = new File([fileOrBlobUrl], fileName, { type: fileOrBlobUrl.type });
            }

            // Compress image if it's an image file
            if (isImage) {
                const options = {
                    maxSizeMB: 0.1,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    initialQuality: 1, // Aim for lossless
                    alwaysKeepResolution: true,
                };
                
                try {
                    const compressedFile = await imageCompression(file, options);
                    console.log('Original:', file.size / 1024, 'KB');
                    console.log('Compressed:', compressedFile.size / 1024, 'KB');
                    file = new File([compressedFile], file.name, { type: compressedFile.type });

                    
                } catch (compressionError) {
                    console.warn('Image compression failed:', compressionError);
                    // Continue with original file if compression fails
                }
            }

            const previewUrl = URL.createObjectURL(file);
            
            setUploadingFiles(prev => {
                const newFiles = [...prev];
                const fileIndex = newFiles.findIndex(f => f.id === fileId);
                if (fileIndex !== -1) {
                    newFiles[fileIndex] = {
                        ...newFiles[fileIndex],
                        file,
                        previewUrl,
                        progress: 0
                    };
                } else {
                    newFiles.push({
                        id: fileId,
                        file,
                        previewUrl,
                        progress: 0
                    });
                }
                return newFiles;
            });

            // Start simulated progress
            // simulateProgress(fileId);

            const controller = new AbortController();
            abortControllersRef.current[fileId] = controller;

            const uploadResponse = await FileUploadQuery(graphqlService, 
                { file }, 
                {
                    context: {
                        fetchOptions: {
                            signal: controller.signal
                        }
                    }
                }
            );

            // Clear the progress interval
            if (progressIntervalsRef.current[fileId]) {
                clearInterval(progressIntervalsRef.current[fileId]);
                delete progressIntervalsRef.current[fileId];
            }

            if (uploadResponse?.uploadFile?.success) {
                // Set progress to 100% on success
                setUploadingFiles(prev => {
                    const newFiles = [...prev];
                    const fileIndex = newFiles.findIndex(f => f.id === fileId);
                    if (fileIndex !== -1) {
                        newFiles[fileIndex] = {
                            ...newFiles[fileIndex],
                            progress: 100
                        };
                    }
                    return newFiles;
                });

                // Clean up resources
                if (previewUrl) {
                    URL.revokeObjectURL(previewUrl);
                }
                delete abortControllersRef.current[fileId];

                onUploadComplete({
                    file: file,
                    url: uploadResponse.uploadFile.url,
                    fileName: uploadResponse.uploadFile.fileName,
                });

                // Remove the file after a short delay to show completion
                setTimeout(() => {
                    setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
                }, 500);
            } else {
                Alerts.error(`Upload failed: ${uploadResponse?.uploadFile?.error || 'Unknown error'}`);
            }
        } catch (error) {
            // Clear the progress interval on error
            if (progressIntervalsRef.current[fileId]) {
                clearInterval(progressIntervalsRef.current[fileId]);
                delete progressIntervalsRef.current[fileId];
            }

            if (error.message === 'Upload cancelled' || error.message === 'signal is aborted without reason') {
                console.log('Upload cancelled by user');
            } else {
                Alerts.error(`Upload error: ${error.message}`);
            }
            if (fileOrBlobUrl.previewUrl) {
                URL.revokeObjectURL(fileOrBlobUrl.previewUrl);
            }
            delete abortControllersRef.current[fileId];
            setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
        }
    };

    const handleCancelUpload = (fileId) => {
        if (abortControllersRef.current[fileId]) {
            abortControllersRef.current[fileId].abort();
            delete abortControllersRef.current[fileId];
        }
        setUploadingFiles(prev => {
            const newFiles = [...prev];
            const fileIndex = newFiles.findIndex(f => f.id === fileId);
            if (fileIndex !== -1) {
                newFiles[fileIndex] = {
                    ...newFiles[fileIndex],
                    progress: 0
                };
            }
            return newFiles;
        });
        const file = uploadingFiles.find(f => f.id === fileId);
        if (file?.previewUrl) {
            URL.revokeObjectURL(file.previewUrl);
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: effectiveAcceptedTypes,
        maxSize: effectiveMaxFileSize,
        maxFiles: maxFiles,
        multiple: maxFiles > 1,
        onDrop: (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles.length > 0) {
                const errorMessage = rejectedFiles[0]?.errors?.[0]?.message || "File upload failed";
                dispatch(setError(errorMessage));
                dispatch(clearFile());
                return;
            }

            // Content-safety: validate type and size from config (image or document)
            if (acceptedFiles.length > 0) {
                for (const file of acceptedFiles) {
                    const result = validateFile(file, isImage ? "image" : "document");
                    if (!result.valid) {
                        Alerts.error(result.message);
                        dispatch(setError(result.message));
                        dispatch(clearFile());
                        return;
                    }
                }
            }

            // Check if adding these files would exceed the maxFiles limit
            if (currentFileCount + acceptedFiles.length > maxFiles) {
                const errorMessage = `Your current plan allows a maximum of ${maxFiles} image${maxFiles > 1 ? 's' : ''}.`;
                Alerts.error(errorMessage);
                dispatch(setError(errorMessage));
                dispatch(clearFile());
                return;
            }

            if (isImage && enableCropper) {
                const file = acceptedFiles[0];
                const objectUrl = URL.createObjectURL(file);
                dispatch(setFile(objectUrl));
                setShowCropper(true);
                dispatch(setError(null));
            } else {
                // Initialize uploading files state with unique IDs
                const filesWithIds = acceptedFiles.map(file => ({
                    id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    file,
                    progress: 0,
                    previewUrl: null
                }));
                setUploadingFiles(filesWithIds);

                // Start processing each file
                filesWithIds.forEach(fileWithId => {
                    handleFileProcessed(fileWithId.file, fileWithId.id);
                });
            }
        },
    });

    const dropzoneShellClass = dashboardUpload
        ? `${adminWizardUploadClasses()} flex cursor-pointer items-center justify-center hover:bg-mistygray transition-colors`
        : fullWidth
        ? `${FIELD_UPLOAD_FULL} flex cursor-pointer items-center justify-center`
        : `h-[144px] w-full rounded flex justify-center bg-white ${applyFieldStyles ? "tp-fields" : ""} border border-solid border-mediumgray cursor-pointer`;

    const renderDropzone = () => (
        <div className="w-full min-w-0">
            <div className="flex items-center justify-center w-full min-w-0">
                <div
                    {...getRootProps()}
                    className={dropzoneShellClass}
                >
                    <input {...getInputProps()} />
                    <div className="my-auto">
                        <div className="flex justify-center">
                            <div className="flex justify-center items-center">
                                <i className="fi fi-rs-inbox-out text-base text-offblack"></i>
                            </div>
                        </div>
                        <div className="flex justify-center">
                            <AuxiliaryText
                                variant={"Placeholder"}
                                className={"mt-1 text-primaryblue"}
                            >
                                Drag & drop or Click to upload
                            </AuxiliaryText>
                        </div>
                        <div className="flex justify-center">
                            <p className="text-xs text-gray-500 mt-2">
                                {`Maximum ${effectiveMaxFileSize / (1024 * 1024)} MB per file`}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderLoading = () => {
        if (loadingType === "circular") {
            return (
                <div className="flex flex-col items-center justify-center p-6">
                    <CircularProgress progress={uploadingFiles[0]?.progress || 0} />
                    <p className="mt-4 text-gray-600">Uploading file...</p>
                </div>
            );
        } else {
            return (
                <></>
                // <div className="flex flex-col gap-3 w-full mt-5">
                //     {uploadingFiles.map((file) => (
                //         <div key={file.id} className="flex gap-3 w-full">
                //             <div className="size-[50px]">
                //                 {file.previewUrl && (
                //                     <img 
                //                         alt="Uploading file" 
                //                         src={file.previewUrl} 
                //                         className="size-full object-fill rounded-lg" 
                //                     />
                //                 )}
                //             </div>
                //             <div className="w-full flex flex-col gap-2">
                //                 <div className="flex items-center justify-between w-full">
                //                     <AuxiliaryText variant={"FieldLabel"}>
                //                         {file.file.name.length > 20 ? file.file.name.substring(0, 20) + '...' : file.file.name}
                //                     </AuxiliaryText>
                //                     <AuxiliaryText variant={"FieldLabel"}>
                //                         {file.progress}%
                //                     </AuxiliaryText>
                //                 </div>
                //                 <div className="w-full h-1.5 bg-white lg:bg-pastalblue rounded-full">
                //                     <div
                //                         className="h-full bg-primaryblue rounded-full transition-all duration-300"
                //                         style={{ 
                //                             width: `${file.progress}%`,
                //                             transition: 'width 0.3s ease-in-out'
                //                         }}
                //                     ></div>
                //                 </div>
                //             </div>
                //             <button 
                //                 type="button" 
                //                 onClick={() => handleCancelUpload(file.id)}
                //                 className="flex items-center justify-center"
                //             >
                //                 <i className="fi fi-rs-cross text-sm cursor-pointer"></i>
                //             </button>
                //         </div>
                //     ))}
                // </div>
            );
        }
    };

    const renderContent = () => {
    console.log(uploadingFiles.length)
        if (loadingType === "progress" && uploadingFiles.length > 0) {
            return (
                <div className="w-full">
                    {renderDropzone()}
                    {error && <p className="text-red-500 mt-2">{error}</p>}
                    <div className="mt-4">
                        {renderLoading()}
                    </div>
                </div>
            );
        }

        if (error) {
            return <p className="text-red-500 mt-2">{error}</p>;
        }

        if (uploadingFiles.length > 0) {
            return renderLoading();
        }

        if (showCropper && isImage && enableCropper) {
            return (
                <ImageCropper 
                    onComplete={(croppedFileBlobURL) => {
                        setShowCropper(false);
                        handleFileProcessed(croppedFileBlobURL, 'cropped-file');
                    }} 
                />
            );
        }

        return renderDropzone();
    };

    return (
        <div className="w-full">
            {renderContent()}
        </div>
    );
};

export default DropzoneUploader; 